import { CerFormRouteOptions } from "@form/cer-form-route-options";
import { CerFormDataNavigation } from "@form/cer-form-navigation.service";
import { CerFormView } from "@form/cer-form-view";
import { CerFormAdapter } from "@form/cer-form-adapter";

import { CerFormPaneView } from "@form/cer-form-panes/cer-form-pane/cer-form-pane-view";
import { CerFormPanesSettings } from "@form/cer-form-panes/cer-form-panes.service";
import { FieldMetadataUtil, FieldMetaDefaults, FieldMetaForeignKey, ViewMetadataUtil } from "@data/cer-data-types";
import { CerRouteData, CerRouteDataEntity } from "@app/app.module-generated";
import { CerFormViewDataLinkConnector } from "./cer-form-view-data-link";

export class CerFormViewLink {
  public parent: string;
  public child: string;
}

export class CerFormViewOptions {
  public firstPosition?: boolean = false;
}

export class CerFormViewSet {
  public routeOptions: CerFormRouteOptions;
  public adapter: CerFormAdapter;
  public panes: CerFormPanesSettings;
  public paneViews: CerFormPaneView[] = [];
  public title: string;

  public add(view: CerFormView, parentView: CerFormView = null,
    parentLink: CerFormViewLink = null, options: CerFormViewOptions = null): CerFormView {
    return this.viewAdd(view, parentView, parentLink, options);
  }

  public viewAdd(view: CerFormView, parentView: CerFormView = null,
    parentLink: CerFormViewLink = null, options: CerFormViewOptions = null): CerFormView {
    if (parentView) {
      view.parentView = parentView;
    }
    view.routeOptions = this.routeOptions;
    this.viewPrepareLink(view, parentLink);
    this.viewPrepare(view);
    const paneView: CerFormPaneView = new CerFormPaneView(view);
    if (options?.firstPosition === true) {
      this.paneViews.unshift(paneView);
    }
    else {
      this.paneViews.push(paneView);
    }
    new CerFormViewDataLinkConnector(this, view).dataLinkViewsAdd();
    return view;
  }

  private viewPrepareLink(v: CerFormView, parentLink: CerFormViewLink = null) {
    if (parentLink && v.viewMetadata) {
      v.fieldMetadata.forEach(f => FieldMetaForeignKey.init(v.viewMetadata, FieldMetaDefaults.init(f, true), true)); // To-do: promote to general scope
      v.viewMetadata.parentForeignKey = parentLink.child;
      v.viewMetadata.parentPrimaryKey = parentLink.parent
      const linkField = FieldMetadataUtil.fieldByName(parentLink.child, v.fieldMetadata);
      if (linkField) {
        linkField.visible = false;
      }
    }
  }

  private viewPrepare(v: CerFormView) {
    const nav = this.routeOptions?.navigation;
    if (v.parentView == null && nav != null) {
      v.parentView = this.routeOptions.parentView;
      if (this.routeOptions.parentView) {
        this.routeOptions.parentView.dataChildrenExists = true;
      }
      const routeEntities = (<CerRouteData>this.routeOptions.routeData)?.entities;
      this.prepareByNavigation(v, nav, routeEntities);
    }
    this.prepareTitle(v, nav);
    const isCustomComponent = (this.routeOptions?.routeAdapterType == null);
    this.preparePaneTarget(v, nav, isCustomComponent);
    v.prepare();
  }

  private prepareTitle(v: CerFormView, nav: CerFormDataNavigation) {
    if (v.title == null && nav?.title) {
      v.title = nav.title;
    }
    const routeData = v.parentView == null ? this.routeOptions?.data() : null;
    if (v.title == null && nav?.filterSingleRow === true) {
      v.title = (routeData?.titleSingular?.length > 0)
        ? routeData.titleSingular
        : v.viewMetadata?.textSingular;
      v.viewMetadata.textSingular = v.title;
    }
    if (v.title == null) {
      v.title = (routeData?.title?.length > 0)
        ? routeData.title
        : v.viewMetadata?.text;
    }
    if (v.title == null) {
      v.title = v.viewMetadata?.dataApiName;
    }
    if (v.viewMetadata) {
      v.viewMetadata.text = v.title;
    }
  }

  private prepareByNavigation(v: CerFormView, nav: CerFormDataNavigation, routeEntities: CerRouteDataEntity[]) {
    if (nav.columnNameParent?.length > 0) {
      this.prepareByNavigationLink(v, nav, routeEntities);
    }
    if (nav.filterSingleRow) {
      if (v.listPaging == 'default' || v.listPaging == null) {
        v.listPaging = 'card';
      }
    }
    // TODO: child data is not refreshed, when parent selection changes
  }

  private prepareByNavigationLink(v: CerFormView, nav: CerFormDataNavigation, routeEntities: CerRouteDataEntity[]) {
    if (v.viewMetadata) {
      v.viewMetadata.parentForeignKey ??= nav.columnNameChild ?? v.viewMetadata.primaryKey as string;
      v.viewMetadata.parentPrimaryKey ??= nav.columnNameParent;
      if (ViewMetadataUtil.linkIsUniqueRow(v.viewMetadata, routeEntities)) {
        nav.filterSingleRow = true;
      }
    }
  }

  private preparePaneTarget(v: CerFormView, nav: CerFormDataNavigation, isCustomComponent: boolean) {
    if (v.paneTarget != null) {
      return;
    }
    if (v.viewType == 'custom' || v.parentView == null) {
      // Main + custom component goes to the main pane)
      v.paneTarget = 'main';
    }
    else if (nav && v.parentView == nav?.parentView) {
      v.paneTarget = nav.paneTarget ?? (nav.filterSingleRow ? 'right' : 'bottom');
    }
    else {
      v.paneTarget = (this.panes?.orientationPrimary == 'vertical') ? 'bottom' : 'right';
    }
  }
}